import type {
  ScorecardBrand,
  ScorecardBrandMapping,
  ScorecardQuestionRow,
  ScorecardRow,
} from "@models/Scorecard";
import { isDatasetMetricRow, isQuestionRow } from "@models/Scorecard";
import { DEFAULT_VISUALIZATION_BASE } from "@models/VisualizationBase";
import { autoMatchNewBrand } from "@utils/scorecard-utils";

/** Adds a mapping for a newly-added brand to every existing Question or
 *  Metric row, auto-matching it against the row's own saved response
 *  categories (see ScorecardQuestionRow/ScorecardDatasetMetricRow.responseCategories)
 *  the same way the row's other brands were matched when it was first
 *  defined. Falls back to "unresolved" when the row predates
 *  responseCategories being saved, or when there's no unambiguous label match. */
export function flagNewBrandOnRows(rows: ScorecardRow[], brand: ScorecardBrand): ScorecardRow[] {
  return rows.map((row) =>
    isQuestionRow(row) || isDatasetMetricRow(row)
      ? {
          ...row,
          brandMappings: [
            ...row.brandMappings,
            autoMatchNewBrand(brand, row.responseCategories ?? [], row.brandMappings),
          ],
        }
      : row,
  );
}

/** Strips a removed brand's mapping/exception entries from every row. */
export function stripBrandFromRows(rows: ScorecardRow[], brandId: string): ScorecardRow[] {
  return rows.map((row) =>
    isQuestionRow(row) || isDatasetMetricRow(row)
      ? {
          ...row,
          brandMappings: row.brandMappings.filter((m) => m.brandId !== brandId),
        }
      : row,
  );
}

/** Question rows whose Scorecard Row Base is an Existing Question Row
 *  reference pointing at rowId. Only Question rows can supply/depend on a
 *  Scorecard Row Base — Metric rows use their saved metric's own Base. */
export function findDependentRows(
  rows: ScorecardRow[],
  rowId: string,
): ScorecardQuestionRow[] {
  return rows.filter(
    (r): r is ScorecardQuestionRow =>
      isQuestionRow(r) &&
      r.rowBase?.kind === "existing_question_row" &&
      r.rowBase.questionRowId === rowId,
  );
}

/** Whether choosing candidateBaseRowId as rowId's Scorecard Row Base would
 *  create a cycle — walks the chain of Existing-Question-Row Base references
 *  starting at candidateBaseRowId and checks whether it ever reaches back to
 *  rowId (or loops on itself, defensively, in case of a pre-existing cycle). */
export function wouldCreateCycle(
  rows: ScorecardRow[],
  rowId: string,
  candidateBaseRowId: string,
): boolean {
  let currentId: string | undefined = candidateBaseRowId;
  const visited = new Set<string>();
  while (currentId) {
    if (currentId === rowId) return true;
    if (visited.has(currentId)) return true;
    visited.add(currentId);
    const row = rows.find(
      (r): r is ScorecardQuestionRow => isQuestionRow(r) && r.id === currentId,
    );
    currentId =
      row?.rowBase?.kind === "existing_question_row" ? row.rowBase.questionRowId : undefined;
  }
  return false;
}

/** Order-independent normalization of a row's brand mappings, so two
 *  functionally-identical mapping sets compare equal regardless of the
 *  order they were built in. */
function normalizeBrandMappings(mappings: ScorecardBrandMapping[]) {
  return [...mappings]
    .sort((a, b) => a.brandId.localeCompare(b.brandId))
    .map((m) => ({
      brandId: m.brandId,
      status: m.status,
      boundResponseId: m.boundResponseId ?? null,
      boundResponseLabel: m.boundResponseLabel ?? null,
      unavailableReason: m.unavailableReason ?? null,
    }));
}

/** Deep-equality check for a Question row's editable fields — used to disable
 *  "Apply changes" when the researcher re-opened a row but hasn't actually
 *  changed anything. */
export function areQuestionRowsEqual(
  a: ScorecardQuestionRow,
  b: ScorecardQuestionRow,
): boolean {
  return (
    a.label === b.label &&
    a.resultType === b.resultType &&
    a.sourceQuestionId === b.sourceQuestionId &&
    a.sourceQuestionKind === b.sourceQuestionKind &&
    (a.metricValueLabel ?? null) === (b.metricValueLabel ?? null) &&
    (a.metricValueCode ?? null) === (b.metricValueCode ?? null) &&
    JSON.stringify(a.valueFocus ?? null) === JSON.stringify(b.valueFocus ?? null) &&
    JSON.stringify(a.rollup ?? null) === JSON.stringify(b.rollup ?? null) &&
    JSON.stringify([...(a.rollupValueIds ?? [])].sort()) ===
      JSON.stringify([...(b.rollupValueIds ?? [])].sort()) &&
    JSON.stringify(normalizeBrandMappings(a.brandMappings)) ===
      JSON.stringify(normalizeBrandMappings(b.brandMappings)) &&
    JSON.stringify(a.visualizationBase ?? DEFAULT_VISUALIZATION_BASE) ===
      JSON.stringify(b.visualizationBase ?? DEFAULT_VISUALIZATION_BASE) &&
    JSON.stringify(a.rowBase ?? null) === JSON.stringify(b.rowBase ?? null)
  );
}

/** Builds a duplicate of a Question row with a new stable identity — deep-
 *  clones every nested mutable structure (Base conditions, rollup arrays,
 *  New Question Base) so the duplicate never shares mutable state with its
 *  source. Existing Question Row Base keeps the same reference (two rows may
 *  legitimately share one Base); New Question Base gets its own new id/
 *  mappings/rollup so it can be edited independently. Extracted from the
 *  Redux slice to keep that file under the file-size limit. */
export function cloneQuestionRowForDuplicate(
  source: ScorecardQuestionRow,
): ScorecardQuestionRow {
  return {
    ...source,
    id: crypto.randomUUID(),
    label: `${source.label} (Copy)`,
    brandMappings: source.brandMappings.map((m) => ({ ...m })),
    responseCategories: source.responseCategories?.map((c) => ({ ...c })),
    visualizationBase: source.visualizationBase
      ? {
          ...source.visualizationBase,
          conditions: source.visualizationBase.conditions.map((c) => ({ ...c })),
        }
      : undefined,
    rollup: source.rollup
      ? {
          ...source.rollup,
          sourceCategoryLabels: [...source.rollup.sourceCategoryLabels],
        }
      : null,
    rollupValueIds: source.rollupValueIds ? [...source.rollupValueIds] : null,
    rowBase:
      source.rowBase?.kind === "new_question_base"
        ? {
            kind: "new_question_base",
            questionBase: {
              ...source.rowBase.questionBase,
              id: crypto.randomUUID(),
              brandMappings: source.rowBase.questionBase.brandMappings.map((m) => ({ ...m })),
              responseCategories: source.rowBase.questionBase.responseCategories?.map((c) => ({
                ...c,
              })),
              rollup: source.rowBase.questionBase.rollup
                ? {
                    ...source.rowBase.questionBase.rollup,
                    sourceCategoryLabels: [
                      ...source.rowBase.questionBase.rollup.sourceCategoryLabels,
                    ],
                  }
                : null,
              rollupValueIds: source.rowBase.questionBase.rollupValueIds
                ? [...source.rowBase.questionBase.rollupValueIds]
                : null,
            },
          }
        : source.rowBase
          ? { ...source.rowBase }
          : source.rowBase,
    rowBaseNeedsAttention: false,
  };
}

/** Deep-equality check for a Metric row's editable fields — used to disable
 *  "Apply changes" when the researcher re-opened a row but hasn't actually
 *  changed anything. */
export function areDatasetMetricRowsEqual(
  a: { label: string; datasetMetricId: string; brandMappings: ScorecardBrandMapping[] },
  b: { label: string; datasetMetricId: string; brandMappings: ScorecardBrandMapping[] },
): boolean {
  return (
    a.label === b.label &&
    a.datasetMetricId === b.datasetMetricId &&
    JSON.stringify(normalizeBrandMappings(a.brandMappings)) ===
      JSON.stringify(normalizeBrandMappings(b.brandMappings))
  );
}
